import { useMemo, useState } from "react";
import { Plus, Clock, CalendarDays, DollarSign } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fmtMoney } from "@/lib/format";

const LINE_TYPES = [
  { id: "labour_hourly", label: "Hourly labour", unit: "hour", icon: Clock,
    defaults: [35, 45], allowCustom: true, presetSuffix: "/hr" },
  { id: "labour_daily", label: "Daily labour", unit: "day", icon: CalendarDays,
    defaults: [280, 360], allowCustom: true, presetSuffix: "/day" },
  { id: "charge", label: "Fixed charge / service fee", unit: "unit", icon: DollarSign,
    defaults: [], allowCustom: true, presetSuffix: "" },
];

/**
 * Dialog to append a manual labour/service line to an existing invoice.
 * Presets: $35/hr and $45/hr (per KINOS standard). Custom rate allowed.
 */
export function AddInvoiceLineDialog({ open, onOpenChange, onSubmit, currency = "USD" }) {
  const [typeId, setTypeId] = useState("labour_hourly");
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [presetRate, setPresetRate] = useState(35);
  const [customRate, setCustomRate] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [notes, setNotes] = useState("");
  const [discPct, setDiscPct] = useState("");
  const [costPrice, setCostPrice] = useState(""); // optional — used to compute margin in the Project Profitability report
  const [submitting, setSubmitting] = useState(false);

  const type = LINE_TYPES.find((t) => t.id === typeId) || LINE_TYPES[0];

  const rate = useCustom
    ? Number(customRate || 0)
    : Number(presetRate || 0);

  const total = useMemo(() => {
    const q = Number(qty || 0);
    const gross = q > 0 && rate > 0 ? q * rate : 0;
    const d = Math.max(0, Math.min(100, Number(discPct || 0)));
    return +(gross * (1 - d / 100)).toFixed(2);
  }, [qty, rate, discPct]);

  const reset = () => {
    setTypeId("labour_hourly");
    setName("");
    setQty("");
    setPresetRate(35);
    setCustomRate("");
    setUseCustom(false);
    setNotes("");
    setDiscPct("");
    setCostPrice("");
  };

  const handleTypeChange = (newId) => {
    setTypeId(newId);
    const t = LINE_TYPES.find((x) => x.id === newId);
    if (t?.defaults?.length) {
      setPresetRate(t.defaults[0]);
      setUseCustom(false);
    } else {
      setUseCustom(true);
    }
  };

  const canSubmit = name.trim() && Number(qty) > 0 && rate > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit({
        line_type: typeId,
        name: name.trim(),
        qty: Number(qty),
        unit_price: rate,
        cost_price: Number(costPrice || 0),
        line_discount_pct: Math.max(0, Math.min(100, Number(discPct || 0))),
        specification: notes.trim(),
      });
      toast.success("Service line added");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add line");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="add-line-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add service / labour line</DialogTitle>
          <DialogDescription className="text-xs">
            Bill consultancy hours, on-site days, or fixed charges on top of the auto-billed items.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Type selector */}
          <div>
            <Label className="overline mb-2 block">Line type</Label>
            <div className="grid grid-cols-3 gap-2">
              {LINE_TYPES.map((t) => {
                const Icon = t.icon;
                const active = typeId === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`line-type-${t.id}`}
                    onClick={() => handleTypeChange(t.id)}
                    className={`flex flex-col items-center gap-1.5 rounded-md border p-3 text-xs font-medium transition ${
                      active
                        ? "border-primary bg-primary/5 text-primary ring-1 ring-primary/40"
                        : "border-border text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="size-4" />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="overline">Description *</Label>
            <Input
              data-testid="line-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. On-site network configuration"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">
                {type.id === "labour_hourly" && "Hours"}
                {type.id === "labour_daily" && "Days"}
                {type.id === "charge" && "Quantity"}
                {" *"}
              </Label>
              <Input
                data-testid="line-qty"
                type="number"
                min="0"
                step="0.25"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="0"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Unit: {type.unit}</p>
            </div>
            <div>
              <Label className="overline">Rate *</Label>
              {type.defaults.length > 0 && !useCustom ? (
                <div className="flex gap-1.5">
                  {type.defaults.map((r) => (
                    <button
                      key={r}
                      type="button"
                      data-testid={`rate-preset-${r}`}
                      onClick={() => setPresetRate(r)}
                      className={`flex-1 rounded-md border px-2 py-1.5 text-sm font-mono transition ${
                        presetRate === r
                          ? "border-primary bg-primary/10 text-primary ring-1 ring-primary/40"
                          : "border-border text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      ${r}{type.presetSuffix}
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid="rate-custom-toggle"
                    onClick={() => { setUseCustom(true); setCustomRate(String(presetRate)); }}
                    className="rounded-md border border-border px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
                  >
                    Custom
                  </button>
                </div>
              ) : (
                <div className="flex gap-1.5 items-center">
                  <div className="relative flex-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                    <Input
                      data-testid="line-custom-rate"
                      className="pl-5 font-mono"
                      type="number"
                      min="0"
                      step="0.01"
                      value={customRate}
                      onChange={(e) => setCustomRate(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  {type.defaults.length > 0 && (
                    <button
                      type="button"
                      onClick={() => { setUseCustom(false); setCustomRate(""); }}
                      className="text-[10px] text-primary hover:underline whitespace-nowrap"
                    >
                      Use preset
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <Label className="overline">Cost per unit <span className="text-muted-foreground text-[10px]">— optional</span></Label>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
              <Input
                data-testid="line-cost-price"
                type="number" min="0" step="0.01"
                value={costPrice}
                onChange={(e) => setCostPrice(e.target.value)}
                placeholder="0.00"
                className="mono pl-5"
              />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Feeds the Project Profitability report (revenue − cost = margin). Not shown on the printed invoice.
            </p>
          </div>

          <div>
            <Label className="overline">Discount (%) <span className="text-muted-foreground text-[10px]">— optional</span></Label>
            <Input
              data-testid="line-discount-pct"
              type="number" min="0" max="100" step="0.5"
              value={discPct}
              onChange={(e) => setDiscPct(e.target.value)}
              placeholder="0"
              className="mono"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Applied to (qty × rate) before invoice-level discount &amp; tax.
            </p>
          </div>

          <div>
            <Label className="overline">Notes (optional)</Label>
            <Textarea
              data-testid="line-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Includes travel, per client's approval on 2026-07-01"
            />
          </div>

          <div
            data-testid="line-total-preview"
            className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 flex items-center justify-between"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-semibold">
              Line total
            </span>
            <span className="font-mono text-lg font-bold text-primary">
              {fmtMoney(total)} {currency}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="line-cancel">
            Cancel
          </Button>
          <Button
            data-testid="line-submit"
            disabled={!canSubmit}
            className="bg-primary hover:bg-primary/90"
            onClick={handleSubmit}
          >
            <Plus className="size-4 mr-1.5" /> Add line
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
